import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { CampaignClass, EOnboardingMilestone, EOnboardingTaskState } from '@feathr/blackbox';
import {
  Button,
  ButtonValid,
  Fieldset,
  ImagePicker,
  Input,
  Modal,
  showError,
  Well,
} from '@feathr/components';
import { CampaignTypeChip } from '@feathr/extender/components';
import ProjectSelect from '@feathr/extender/components/ProjectSelect';
import { useLocalUrl } from '@feathr/extender/state';
import { useAccount, useStore } from '@feathr/extender/state';
import { logUserEvents } from '@feathr/hooks';

import { useCampaignForm } from './useCampaignForm';

interface ICreateAdCampaignModalProps {
  onClose: () => void;
}

function CreateAdCampaignModal({
  onClose,
}: Readonly<ICreateAdCampaignModalProps>): JSX.Element | null {
  const { t } = useTranslation();
  const { Campaigns, OnboardingTasks } = useStore();
  const account = useAccount();

  const history = useHistory();

  // Use the campaign form hook for MobX-based state management
  const { campaign, logoUrl, setLogoUrl, validationErrors } = useCampaignForm(t);
  const localUrl = useLocalUrl();

  const milestone = EOnboardingMilestone.OmniCampaign;
  const campaignClass = CampaignClass.Omni;

  const handleComplete = useCallback(async (): Promise<void> => {
    const tasks = await OnboardingTasks.getMilestoneTasks([milestone]);
    const task = tasks?.[0];

    try {
      const projectId = campaign.get('event');

      if (!projectId) {
        throw new Error(t('Please select a project.'));
      }
      const response = await Campaigns.addCampaign(campaignClass, projectId, {
        name: campaign.name,
      });

      account.set({ logo: logoUrl });
      await account.patchDirty();

      // Send AppCues event
      logUserEvents({
        [`onboarding_task_clicked_${task.type}`]: {
          task_type: task.type,
          task_state: task.state,
          milestone: milestone,
          task_title: 'Publish ad campaign',
        },
      });

      if (task.state === EOnboardingTaskState.NotStarted) {
        OnboardingTasks.updateMilestoneTask(task.id, EOnboardingTaskState.InProgress);
      }

      history.push(`${localUrl(response.getItemUrl('edit'))}?onboarding=true`);
    } catch (error) {
      showError(error, t);
    } finally {
      onClose();
    }

    onClose();
  }, [campaign, onClose, account, logoUrl]);

  return (
    <Modal
      description={t('Set up your first advertising campaign')}
      onClose={onClose}
      opened={true}
      rightActions={
        <>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <ButtonValid errors={validationErrors} onClick={handleComplete} type={'primary'}>
            {t('Create')}
          </ButtonValid>
        </>
      }
      size={'default'}
      title={t('Create Ad Campaign')}
    >
      <Fieldset>
        <Stack gap={16}>
          <Well label={t('Campaign type')} layout={'horizontal'}>
            <CampaignTypeChip cls={campaignClass} />
          </Well>

          <ProjectSelect
            label={t('Project')}
            onChangeSingle={(id): void => campaign.set({ event: id || '' })}
            value={campaign.get('event')}
          />
          <Input
            attribute={'name'}
            label={t('Campaign Name')}
            model={campaign}
            placeholder={t('Enter a campaign name...')}
          />
          <ImagePicker
            buttonText={t('Upload organization logo')}
            helpText={t(
              "Upload your organization's logo. This is required prior to publishing a campaign. PNG, GIF, or JPG files accepted.",
            )}
            label={t('Organization logo')}
            onPick={(url): void => {
              setLogoUrl(url);
            }}
            value={logoUrl}
          />
        </Stack>
      </Fieldset>
    </Modal>
  );
}

export default observer(CreateAdCampaignModal);
